import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Trans, useTranslation } from "react-i18next";
import PlacesAutocomplete, { geocodeByPlaceId } from "react-places-autocomplete";

import { FloatingLabelInput, PhoneInput, Columns, Column } from "@aviary";
import { AddressPulldownView } from "@shared/components/AddressPulldown/AddressPulldownView";
import { GoogleAddress } from "@shared/components/AddressPulldown/GoogleAddress";
import { useSharedGlobalConfig } from "@shared/hooks";
import { l } from "@shared/locales/i18n";
import type { AddressInformationData, AddressInformationErrors } from "@shared/types/fields";
import type {
  BusinessAddressAttributes,
  StripePersonAttributes,
} from "@shared/types/graphqlGenerated";

import { IconWrapper, StateSelect, ValidationSummary } from "../../../components";

import type { StatesData } from "./StatesInfo.query";
import { useStatesInfoQuery } from "./StatesInfo.query";

import * as styles from "./AddressInfoFields.styles";

const placesAutocompeteBoilerplate = (
  addressInformationData:
    | AddressInformationData
    | BusinessAddressAttributes
    | StripePersonAttributes,
  handleSetAddressInformation: (
    addressInfo: AddressInformationData | BusinessAddressAttributes | StripePersonAttributes
  ) => void,
  data: StatesData
) => {
  const { streetAddress, address1, phone } = (addressInformationData || {}) as any;
  const handleBlur = (onBlur, suggestions) => async e => {
    onBlur(e);

    const suggestionActive = suggestions.some(suggestion => suggestion.active);
    if (suggestionActive) {
      await onSuggestionSelect(streetAddress ?? address1);
    }
  };

  const onSuggestionSelect: any = async (address: string, placeId: string) => {
    const placeDetails = await geocodeByPlaceId(placeId);
    const streetNumber = address.split(" ")[0];
    const states = data?.viewer.states;
    handleSetAddressInformation({
      phone: phone ?? "",
      ...GoogleAddress(placeDetails, streetNumber, states),
    });
  };

  return { onSuggestionSelect, handleBlur };
};

interface Props {
  addressInformationData: AddressInformationData;
  addressInformationErrors: AddressInformationErrors;
  handleSetAddressInformation: (addressInfo: AddressInformationData) => void;
  setIsDirty?: (boolean) => void;
  isFullWidth?: boolean;
  showStreetAddress2?: boolean;
  showPhoneNumber?: boolean;
  useSingleColumn?: boolean;
  isMarginLess?: boolean;
}

const AddressInfoFields = ({
  addressInformationErrors,
  addressInformationData,
  handleSetAddressInformation,
  setIsDirty,
  isFullWidth = false,
  showStreetAddress2 = false,
  showPhoneNumber = true,
  useSingleColumn = false,
  isMarginLess = false,
}: Props) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();

  const { data } = useStatesInfoQuery();
  const updateAddressInfo = (id, value) => {
    handleSetAddressInformation({ ...addressInformationData, [id]: value });
  };
  const { country } = useSharedGlobalConfig();

  const onAddressChange = e => {
    const { value, id } = e.target;
    updateAddressInfo(id, value);
    if (setIsDirty) {
      setIsDirty(true);
    }
  };

  const onPhoneNumberChange = value => {
    updateAddressInfo("phone", value.replace("+", ""));
  };

  const {
    streetAddress: streetAddressErrors,
    streetAddress2: streetAddress2Errors,
    city: cityErrors,
    region: regionErrors,
    postalCode: postalCodeErrors,
    phone: phoneErrors,
  } = addressInformationErrors !== null &&
  !!addressInformationErrors.fields &&
  addressInformationErrors.fields;

  const topLevelErrors = addressInformationErrors?.message && [addressInformationErrors.message];

  const renderPhoneTooltip = () => {
    return (
      <Trans i18nKey={l.sharedSettings.tax.addressInfo.phoneTooltip}>
        We're here to help. Providing this information helps our team give our users a{" "}
        <strong>faster</strong> support experience
      </Trans>
    );
  };

  const regionLabel = isUS
    ? t(l.sharedSettings.tax.addressInfo.state)
    : t(l.sharedSettings.tax.addressInfo.province);

  const postalCodeLabel = isUS
    ? t(l.sharedSettings.tax.addressInfo.zipcode)
    : t(l.sharedSettings.tax.addressInfo.postalCode);

  const { onSuggestionSelect, handleBlur } = placesAutocompeteBoilerplate(
    addressInformationData,
    handleSetAddressInformation,
    data
  );

  return (
    <Columns isMultiline isMarginless={isMarginLess}>
      <Column columnWidth={useSingleColumn && !isFullWidth ? 6 : 12}>
        <PlacesAutocomplete
          value={addressInformationData?.streetAddress ?? ""}
          onChange={value => onAddressChange({ target: { id: "streetAddress", value } })}
          onSelect={onSuggestionSelect}
          searchOptions={{
            componentRestrictions: { country: country === "United States" ? "us" : "ca" },
          }}
          shouldFetchSuggestions={addressInformationData?.streetAddress?.length > 3}
          onError={(_, clearSuggestions) => {
            clearSuggestions();
          }}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
            const { onBlur, onChange, onKeyDown, value } = getInputProps();

            return (
              <AddressPulldownView
                data-testid="street-address-input"
                suggestions={suggestions}
                onBlur={handleBlur(onBlur, suggestions) as any}
                onChange={onChange}
                onKeyDown={onKeyDown}
                getSuggestionItemProps={getSuggestionItemProps}
                value={value}
                id="streetAddress"
                label={t(l.sharedSettings.tax.addressInfo.streetAddress)}
                name={t(l.sharedSettings.tax.addressInfo.streetAddress)}
                errors={streetAddressErrors}
                loading={loading}
                maxLength={50}
                data-e2e="street-address"
              />
            );
          }}
        </PlacesAutocomplete>
      </Column>
      {showStreetAddress2 && (
        <Column columnWidth={isFullWidth ? 12 : 6}>
          <FloatingLabelInput
            type="text"
            id="streetAddress2"
            label={t(l.sharedSettings.tax.addressInfo.streetAddress2)}
            name={t(l.sharedSettings.tax.addressInfo.streetAddress2)}
            value={addressInformationData?.streetAddress2 ?? ""}
            handleChange={onAddressChange}
            errors={streetAddress2Errors}
            maxLength={50}
            data-e2e="street-address-2"
          />
        </Column>
      )}
      <Column columnWidth={isFullWidth ? 12 : 6}>
        <FloatingLabelInput
          data-testid="city-input"
          type="text"
          id="city"
          name={t(l.sharedSettings.tax.addressInfo.city)}
          label={t(l.sharedSettings.tax.addressInfo.city)}
          value={addressInformationData?.city ?? ""}
          handleChange={onAddressChange}
          required
          errors={cityErrors}
          maxLength={30}
          data-e2e="city"
        />
      </Column>
      <Column columnWidth={isFullWidth ? 12 : 6}>
        <StateSelect
          id="region"
          onChange={onAddressChange}
          value={addressInformationData?.region ?? ""}
          label={regionLabel}
          name={regionLabel}
          placeholder={t(l.sharedCommon.Select)}
          isRequired
          errors={regionErrors}
          data-e2e="region"
        />
      </Column>
      <Column columnWidth={isFullWidth ? 12 : 6}>
        <FloatingLabelInput
          type="text"
          id="postalCode"
          label={postalCodeLabel}
          name={postalCodeLabel}
          value={addressInformationData?.postalCode ?? ""}
          handleChange={onAddressChange}
          required
          errors={postalCodeErrors}
          data-e2e="postal-code"
        />
      </Column>
      {showPhoneNumber && (
        <Column columnWidth={isFullWidth ? 12 : 6}>
          <IconWrapper icon={faInfoCircle} tooltipMessage={renderPhoneTooltip()}>
            <div css={styles.phoneInput}>
              <PhoneInput
                id="phone"
                label={t(l.sharedSettings.tax.addressInfo.phoneNumber)}
                name={t(l.sharedSettings.tax.addressInfo.phoneNumber)}
                aria-label={t(l.sharedSettings.tax.addressInfo.phoneNumber)}
                value={addressInformationData?.phone ?? ""}
                onChange={onPhoneNumberChange}
                required
                errors={phoneErrors}
                preferredCountryCode={isUS ? "US" : "CA"}
                data-e2e="phone"
              />
            </div>
          </IconWrapper>
        </Column>
      )}
      <ValidationSummary errors={topLevelErrors} css={styles.validationSummary} />
    </Columns>
  );
};

export { AddressInfoFields, type Props as AddressInfoFieldsProps, placesAutocompeteBoilerplate };
